import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { calendarDateSchema } from '../validators/calendar-date.validator.js'
import {
  siteChangeActionSchema,
  siteChangeEntitySchema,
  type SiteChangeEntity,
} from '../enums/site-change.enum.js'
import { jobTypeSchema } from '../enums/job-type.enum.js'
import {
  irrigationEquipmentKindSchema,
  waterSourceKindSchema,
} from '../enums/irrigation.enum.js'
import { recordedGeoPointSchema, siteBoundarySchema } from './geo.dto.js'
import { treeJobDtoSchema } from './tree.dto.js'
import { treeSpeciesMarkSchema } from './tree-species.dto.js'
import { siteVisitDtoSchema } from './site-visit.dto.js'
import {
  areaSnapshotOnWire,
  boundarySnapshotOnWire,
  equipmentSnapshotOnWire,
  timerSnapshotOnWire,
  treeSnapshotOnWire,
  waterSourceSnapshotOnWire,
  zoneSnapshotOnWire,
} from './site-record-snapshot.js'

/**
 * One change to a site's record: a tree came down, a timer moved, the
 * boundary was redrawn. See docs/site-visits.md.
 *
 * `before` and `after` are that kind of record's tracked fields. Which
 * fields, and what each one holds, follows the entity, so a zone's heads
 * cannot be read as a tree's trunk.
 */
const changeFields = {
  id: uuidSchema,
  entityId: z.string(),
  action: siteChangeActionSchema,
  /** The record having been wrong, rather than the site changing. */
  isCorrection: z.boolean(),
  /** What it is called: "#41", "Zone 4", "Backflow". */
  subject: z.string(),
  /** When it happened in the world, `YYYY-MM-DD`: the visit's day by default. */
  effectiveOn: calendarDateSchema,
  /** The visit it was recorded on; null for one made with none open. */
  visitId: uuidSchema.nullable(),
  /**
   * The Housecall Pro job that made the change: a tree its removal job took
   * down, with the job's number when HCP gave one. Null for every change
   * made by hand. Defaulted, as `treeWork` is.
   */
  job: z.object({ number: z.string().nullable() }).nullable().default(null),
  recordedAt: z.string().datetime(),
  recordedByName: z.string().nullable(),
}

function changeVariant<E extends SiteChangeEntity, Snapshot extends z.ZodType>(
  entity: E,
  snapshot: Snapshot,
) {
  return z.object({
    ...changeFields,
    entity: z.literal(entity),
    /**
     * The tracked fields that changed, as they were and as they became.
     * Every one of them for an addition (`after`) and a removal (`before`).
     * Null when that side was not stored, or does not read as this kind of
     * record.
     */
    before: snapshot,
    after: snapshot,
  })
}

export const siteChangeDtoSchema = z.discriminatedUnion('entity', [
  changeVariant('BOUNDARY', boundarySnapshotOnWire),
  changeVariant('AREA', areaSnapshotOnWire),
  changeVariant('TREE', treeSnapshotOnWire),
  changeVariant('WATER_SOURCE', waterSourceSnapshotOnWire),
  changeVariant('TIMER', timerSnapshotOnWire),
  changeVariant('ZONE', zoneSnapshotOnWire),
  changeVariant('EQUIPMENT', equipmentSnapshotOnWire),
])

export type SiteChangeDto = z.infer<typeof siteChangeDtoSchema>

/**
 * Work done on one tree by a Housecall Pro job made from an accepted
 * proposal: the kinds of pruning and the treatments, or its removal. See
 * docs/site-visits.md, "Tree work done".
 */
export const treeWorkDtoSchema = z.object({
  id: uuidSchema,
  treeId: uuidSchema,
  /** What the tree is called: "#41". */
  subject: z.string(),
  jobTypes: z.array(jobTypeSchema),
  treatments: z.array(z.string()),
  removal: z.boolean(),
  /** The day HCP finished the job, `YYYY-MM-DD`. */
  completedOn: calendarDateSchema,
  /** HCP's job number, what the office calls the job by. */
  jobNumber: z.string().nullable(),
})

export type TreeWorkDto = z.infer<typeof treeWorkDtoSchema>

/** A site's history: its visits, and the changes to its record, newest first. */
export const siteHistoryDtoSchema = z.object({
  siteId: uuidSchema,
  visits: z.array(siteVisitDtoSchema),
  changes: z.array(siteChangeDtoSchema),
  /**
   * Tree work done, newest first. Defaulted, so a page newer than the API it
   * reads still reads it.
   */
  treeWork: z.array(treeWorkDtoSchema).default([]),
  /**
   * What the ids in the snapshots are called: species, parts, timers, zones
   * and water sources, so "species corrected to Pecan" needs no second
   * request. An id missing here has no name to give.
   */
  names: z.record(z.string(), z.string()),
})

export type SiteHistoryDto = z.infer<typeof siteHistoryDtoSchema>

const pinFields = {
  id: z.string(),
  ...recordedGeoPointSchema.shape,
}

/**
 * A site as it stood on a past visit's day: its record with every real
 * change since undone, and that visit's findings on its trees. Corrections
 * apply all the way back, so a tree pinned in the wrong place then is drawn
 * where it really is. See docs/site-visits.md, "The map as of a visit".
 */
export const siteMapAsOfDtoSchema = z.object({
  siteId: uuidSchema,
  visit: siteVisitDtoSchema,
  /** The day it is drawn as of, `YYYY-MM-DD`: the visit's. */
  asOf: calendarDateSchema,
  boundary: siteBoundarySchema.nullable(),
  areas: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      boundary: siteBoundarySchema,
    }),
  ),
  trees: z.array(
    z.object({
      ...pinFields,
      label: z.string(),
      species: treeSpeciesMarkSchema.nullable(),
      /** What the visit found and recommended, as the pin draws it. */
      jobs: z.array(treeJobDtoSchema),
      estimatedLaborMinutes: z.number().int().nonnegative().nullable(),
      problems: z.array(z.object({ id: z.string() })),
    }),
  ),
  waterSources: z.array(
    z.object({
      ...pinFields,
      kind: waterSourceKindSchema,
      label: z.string().nullable(),
    }),
  ),
  timers: z.array(z.object({ ...pinFields, label: z.string().nullable() })),
  zones: z.array(
    z.object({
      id: z.string(),
      timerId: z.string(),
      number: z.number().int(),
      boundary: siteBoundarySchema.nullable(),
    }),
  ),
  equipment: z.array(
    z.object({
      ...pinFields,
      kind: irrigationEquipmentKindSchema,
      name: z.string().nullable(),
      zoneId: z.string().nullable(),
      /** The valve box it sat in; drawn at the box rather than on its own. */
      boxId: z.string().nullable().default(null),
    }),
  ),
})

export type SiteMapAsOfDto = z.infer<typeof siteMapAsOfDtoSchema>

/**
 * One record's history, for its own sheet: its changes, the work done on
 * it, and what each visit found on it, newest first (docs/site-visits.md,
 * "The site page").
 */
export const recordHistoryDtoSchema = z.object({
  entity: siteChangeEntitySchema,
  entityId: z.string(),
  changes: z.array(siteChangeDtoSchema),
  /** Work Housecall Pro jobs did on it; a tree's only. */
  treeWork: z.array(treeWorkDtoSchema),
  /** What each visit that looked at it found and recommended. */
  findings: z.array(
    z.object({
      visitId: uuidSchema,
      /** The visit's day, `YYYY-MM-DD`. */
      startedOn: calendarDateSchema,
      /** "Mistletoe", "Bad nozzle ×3". */
      problems: z.array(z.string()),
      /** "Crown Cleaning", "Removal": a tree's recommended work. */
      work: z.array(z.string()),
    }),
  ),
  /** What the ids in the changes' snapshots are called. */
  names: z.record(z.string(), z.string()),
})

export type RecordHistoryDto = z.infer<typeof recordHistoryDtoSchema>
